/**
 * Sovereign Powers: Will, casting, and the ground effects they leave behind.
 *
 * A cast resolves in two halves. The immediate half happens now; anything with a
 * telegraph is pushed into `world.zones` as a Strike that lands later, so the
 * red circle on the ground is a real object the simulation is going to honour,
 * not a decoration the renderer invents.
 */

import { alliedWith, Phase, Team, UF, DamageType, UnitState } from '../types.ts';
import { EV, LogTone, pushEvent, pushLog } from '../events.ts';
import { spawnUnit, statsOf, ZoneKind, type World, type Zone } from '../world.ts';
import { damageUnit, damageWall, killUnit } from './combat.ts';
import { power, powerOrNull, type PowerDef } from '../../data/powers.ts';
import { unitTypeIndex } from '../../data/units.ts';
import { buildingType } from '../../data/buildings.ts';
import { nextFloat } from '../../core/rng.ts';
import { MAP_W, MAP_H } from '../constants.ts';
import { WallKind } from '../types.ts';

const WILL_REGEN_SIEGE = 3.1;
const WILL_REGEN_BUILD = 0.9;

const SKELETON = () => unitTypeIndex('summon_skeleton');
const BRAMBLE = () => unitTypeIndex('summon_bramble');

export interface CastResult {
  ok: boolean;
  reason: string;
}

export function powerTier(w: World, id: string): number {
  return w.powerTiers[id] ?? 0;
}

/** Effective numbers for a power at its current upgrade tier. */
export function resolvePower(w: World, def: PowerDef) {
  let damage = def.damage;
  let radius = def.radius;
  let duration = def.duration;
  let cooldown = def.cooldown;
  let cost = def.cost;
  const extras: string[] = [];

  const tier = powerTier(w, def.id);
  for (let i = 0; i < tier; i++) {
    const t = def.tiers[i];
    if (!t) continue;
    damage *= t.damageMul ?? 1;
    radius *= t.radiusMul ?? 1;
    duration *= t.durationMul ?? 1;
    cooldown *= t.cooldownMul ?? 1;
    cost *= t.costMul ?? 1;
    if (t.extra) extras.push(t.extra);
  }
  const willMul = w.faction.bonuses.willRegen ?? 1;
  if (w.faction.id === 'vigil') {
    cost *= 0.8;
    damage *= 1.25;
  }
  damage *= w.mods.powerDamage;
  radius *= w.mods.powerRadius;
  cooldown *= w.mods.powerCooldown;
  return { damage, radius, duration, cooldown, cost: cost / Math.max(0.6, willMul), extras };
}

export function canCast(w: World, id: string): CastResult {
  const def = powerOrNull(id);
  if (!def) return { ok: false, reason: 'No such power.' };
  if (!w.unlockedPowers.includes(id)) return { ok: false, reason: 'Not yet yours.' };
  if ((w.powerCooldowns[id] ?? 0) > 0) return { ok: false, reason: 'Still cooling.' };
  const r = resolvePower(w, def);
  if (w.will < r.cost) return { ok: false, reason: 'Not enough Will.' };
  return { ok: true, reason: '' };
}

export function castPower(w: World, id: string, x: number, y: number, dirX = 1, dirY = 0): CastResult {
  const check = canCast(w, id);
  if (!check.ok) return check;
  const def = power(id);
  const r = resolvePower(w, def);

  w.will -= r.cost;
  w.powerCooldowns[id] = r.cooldown;

  unleashPower(w, id, x, y, dirX, dirY);
  return { ok: true, reason: '' };
}

/**
 * A power's payload, with no gatekeeping: no Will, no unlock check, no shared
 * cooldown. `castPower` pays the Sovereign's price and then calls this; a
 * hero's battle skill pays its own price and calls this too.
 *
 * Returns the cooldown the effect *would* carry, so a caller that keeps its own
 * timers can scale it rather than inventing a second number.
 */
export function unleashPower(w: World, id: string, x: number, y: number, dirX = 1, dirY = 0, casterTeam: number = Team.Player): number {
  const def = powerOrNull(id);
  if (!def) return 0;
  const r = resolvePower(w, def);
  statsOf(w).powersCast++;

  pushEvent(w.events, EV.Power, x, y, r.radius, def.age, casterTeam, dirX, dirY, def.id);

  if (def.delay > 0) {
    w.zones.push(zone(ZoneKind.Strike, x, y, r.radius, def.delay, r.damage, def.friendlyFire, def.id, casterTeam));
    pushEvent(w.events, EV.Telegraph, x, y, r.radius, Math.round(def.delay * 100), Team.Invader, 0, 0, def.id);
    // Powers that also start a lingering field do so when they land.
    return r.cooldown;
  }

  applyImmediate(w, def, r, x, y, dirX, dirY, casterTeam);
  return r.cooldown;
}

function zone(
  kind: number,
  x: number,
  y: number,
  r: number,
  t: number,
  power: number,
  friendlyFire: boolean,
  src: string,
  team: number = Team.Player,
): Zone {
  return { kind, x, y, r, t, life: t, power, team, friendlyFire, src };
}

function applyImmediate(
  w: World,
  def: PowerDef,
  r: ReturnType<typeof resolvePower>,
  x: number,
  y: number,
  dirX: number,
  dirY: number,
  team: number,
): void {
  const u = w.units;

  switch (def.id) {
    case 'rally_banner': {
      forEachFriendly(w, x, y, r.radius, (i) => {
        u.rallyT[i] = Math.max(u.rallyT[i], r.duration);
        u.morale[i] = 1;
        if (r.extras.includes('heal')) u.hp[i] = Math.min(u.maxHp[i], u.hp[i] + (u.maxHp[i] - u.hp[i]) * 0.25);
        if (u.state[i] === UnitState.Routing) {
          u.state[i] = UnitState.Idle;
          u.stateT[i] = 0;
        }
      }, team);
      pushEvent(w.events, EV.Rally, x, y, r.radius, 0, team);
      break;
    }
    case 'sanctified_ground': {
      const z = zone(ZoneKind.Heal, x, y, r.radius, r.duration, r.damage || 1, false, def.id, team);
      z.power = 0.05 * (r.damage === 0 ? 1 : 1);
      if (r.extras.includes('cleanse')) z.src = 'sanctified_ground_cleanse';
      w.zones.push(z);
      break;
    }
    case 'time_in_amber':
      w.zones.push(zone(ZoneKind.Slow, x, y, r.radius, r.duration, 0.6, false, def.id, team));
      break;
    case 'quicklime':
      w.zones.push(zone(ZoneKind.Sunder, x, y, r.radius, r.duration, r.extras.includes('burn') ? 8 : 0, false, def.id, team));
      break;
    case 'iron_resolve': {
      forEachFriendly(w, x, y, r.radius, (i) => {
        u.shieldT[i] = Math.max(u.shieldT[i], r.duration);
        u.morale[i] = Math.max(u.morale[i], 0.9);
      }, team);
      w.zones.push(zone(ZoneKind.Shield, x, y, r.radius, r.duration, 0.55, false, def.id, team));
      break;
    }
    case 'blood_price': {
      const costFrac = r.extras.includes('lifesteal') ? 0.07 : 0.12;
      forEachFriendly(w, x, y, r.radius, (i) => {
        u.hp[i] = Math.max(1, u.hp[i] * (1 - costFrac));
        u.rallyT[i] = Math.max(u.rallyT[i], r.duration);
      }, team);
      break;
    }
    case 'hearth_light': {
      if (team === Team.Player) w.morale = Math.min(1.4, w.morale + 0.15);
      w.zones.push(zone(ZoneKind.Rally, x, y, r.radius, r.duration, 0, false, def.id, team));
      if (team === Team.Player && r.extras.includes('will')) w.will = Math.min(w.willMax, w.will + 8);
      break;
    }
    case 'grave_call': {
      graveCall(w, x, y, r, team);
      break;
    }
    case 'pale_summons': {
      const count = r.extras.includes('more') ? 18 : 12;
      summonDead(w, x, y, count, r.duration, r.extras.includes('more') ? 1.7 : 1, team);
      break;
    }
    case 'wall_of_bramble': {
      brambleLine(w, x, y, dirX, dirY, r, team);
      break;
    }
    case 'gale': {
      cone(w, x, y, dirX, dirY, r.radius, (i, d) => {
        const push = (r.extras.includes('slam') ? 9 : 6.5) * (1 - d / r.radius);
        u.knockX[i] += dirX * push;
        u.knockY[i] += dirY * push;
        u.stunT[i] = Math.max(u.stunT[i], r.duration);
        damageUnit(w, i, 20, DamageType.Blunt, x, y, -1);
      }, team);
      break;
    }
    /**
     * The Sovereign's one aggressive verb, and it kills nothing.
     *
     * Written to stand where Starfall stood — the Hearth grants it and the
     * opening kit carries it — and built on the two fields Gale already uses, so
     * there is no new mechanic and no new state: a pull is a push with the sign
     * flipped, toward the reticle instead of away from it. The stagger is what
     * makes it worth anything, and the fact that it does no damage at all is
     * what makes it *ask* for a defence rather than be one.
     */
    case 'haul_down': {
      w.spatial.query(x, y, r.radius, u, (i, d2) => {
        if (alliedWith(u.team[i], team)) return;
        const dx = x - u.x[i];
        const dy = y - u.y[i];
        const d = Math.sqrt(d2) || 1;
        // Strongest at the rim, so the power gathers a crowd rather than
        // scattering the middle of one through the far side.
        const pull = 5.5 * (0.35 + 0.65 * (d / r.radius));
        u.knockX[i] += (dx / d) * pull;
        u.knockY[i] += (dy / d) * pull;
        u.stunT[i] = Math.max(u.stunT[i], r.duration);
        if (r.extras.includes('sunder')) {
          u.shieldT[i] = 0;
          u.flags[i] &= ~UF.Shielded;
        }
      });
      if (r.extras.includes('sunder')) {
        w.zones.push(zone(ZoneKind.Sunder, x, y, r.radius, 6, 0, false, def.id, team));
      }
      pushEvent(w.events, EV.Impact, x, y, r.radius, 93, team);
      break;
    }
    case 'last_stand': {
      forEachFriendly(w, x, y, r.radius, (i) => {
        u.shieldT[i] = Math.max(u.shieldT[i], r.duration);
        u.rallyT[i] = Math.max(u.rallyT[i], r.duration);
        u.morale[i] = 1;
      }, team);
      break;
    }
    default:
      // Powers with a telegraph resolve in `landStrike`.
      break;
  }
}

/** A telegraphed power finally arriving. */
function landStrike(w: World, z: Zone): void {
  const def = powerOrNull(z.src);
  if (!def) return;
  const r = resolvePower(w, def);
  const u = w.units;

  switch (def.id) {
    case 'tremor': {
      w.spatial.query(z.x, z.y, z.r, u, (i, d2) => {
        if (alliedWith(u.team[i], z.team)) return;
        const dx = u.x[i] - z.x;
        const dy = u.y[i] - z.y;
        const l = Math.hypot(dx, dy) || 1;
        damageUnit(w, i, z.power, DamageType.Blunt, z.x, z.y, -1, dx / l, dy / l);
        if ((u.flags[i] & UF.Large) === 0) u.stunT[i] = Math.max(u.stunT[i], r.duration);
        else u.stunT[i] = Math.max(u.stunT[i], r.duration * 0.4);
        void d2;
      });
      if (r.extras.includes('siege')) structuresIn(w, z.x, z.y, z.r, 200, DamageType.Siege);
      break;
    }
    case 'toll_the_bell': {
      const hitsUndead = r.extras.includes('undead');
      w.spatial.query(z.x, z.y, z.r, u, (i) => {
        if (alliedWith(u.team[i], z.team)) return;
        const f = u.flags[i];
        if ((f & (UF.Siege | UF.Champion)) !== 0) return;
        if (!hitsUndead && (f & UF.Undead) !== 0) return;
        u.state[i] = UnitState.Routing;
        u.stateT[i] = r.duration;
        u.morale[i] = 0;
        u.fearT[i] = r.duration;
        pushEvent(w.events, EV.Rout, u.x[i], u.y[i], 1, u.typeId[i], u.team[i]);
      });
      break;
    }
    case 'shatterstone': {
      structuresIn(w, z.x, z.y, z.r, z.power, DamageType.Siege, true);
      w.spatial.query(z.x, z.y, z.r, u, (i) => {
        if (alliedWith(u.team[i], z.team)) return;
        if ((u.flags[i] & (UF.Siege | UF.Large)) === 0) return;
        damageUnit(w, i, z.power * 0.6, DamageType.Siege, z.x, z.y, -1);
      });
      if (r.extras.includes('repair') && alliedWith(w.wallTeam, z.team)) repairWalls(w, z.x, z.y, z.r, 200);
      break;
    }
    case 'pale_summons': {
      summonDead(w, z.x, z.y, r.extras.includes('more') ? 18 : 12, r.duration, 1, z.team);
      break;
    }
    default:
      break;
  }
}

// ── Zone stepping ──────────────────────────────────────────────────────────

export function stepPowers(w: World, dt: number): void {
  // Will.
  const regen =
    (w.phase === Phase.Siege ? WILL_REGEN_SIEGE : WILL_REGEN_BUILD) *
    (w.faction.bonuses.willRegen ?? 1) *
    w.mods.willRegen;
  w.will = Math.min(w.willMax, w.will + regen * dt);

  for (const id of Object.keys(w.powerCooldowns)) {
    if (w.powerCooldowns[id] > 0) {
      w.powerCooldowns[id] = Math.max(0, w.powerCooldowns[id] - dt);
    }
  }

  const u = w.units;
  for (let i = w.zones.length - 1; i >= 0; i--) {
    const z = w.zones[i];
    z.t -= dt;

    switch (z.kind) {
      case ZoneKind.Strike:
        if (z.t <= 0) landStrike(w, z);
        break;
      case ZoneKind.Poison:
        w.spatial.query(z.x, z.y, z.r, u, (j) => {
          if (!z.friendlyFire && u.team[j] === z.team) return;
          u.poisonT[j] = Math.max(u.poisonT[j], 1.2);
          if (z.src === 'rot_wind_slow') u.slowT[j] = Math.max(u.slowT[j], 1.2);
          damageUnit(w, j, z.power * dt, DamageType.Poison, z.x, z.y, -1);
        });
        break;
      case ZoneKind.Heal:
        w.spatial.query(z.x, z.y, z.r, u, (j) => {
          if (u.team[j] !== z.team) return;
          u.hp[j] = Math.min(u.maxHp[j], u.hp[j] + u.maxHp[j] * z.power * dt);
          u.shieldT[j] = Math.max(u.shieldT[j], 0.4);
          if (z.src === 'sanctified_ground_cleanse') {
            u.poisonT[j] = 0;
            u.fearT[j] = 0;
            u.madnessT[j] = 0;
          }
        });
        break;
      case ZoneKind.Slow:
        w.spatial.query(z.x, z.y, z.r, u, (j) => {
          if (u.team[j] === z.team) return;
          u.slowT[j] = Math.max(u.slowT[j], 0.5);
        });
        break;
      case ZoneKind.Fire: {
        // Periodic strikes rather than continuous damage — it reads better.
        const period = 0.5;
        const before = Math.floor((z.life - z.t - dt) / period);
        const after = Math.floor((z.life - z.t) / period);
        if (after > before) {
          const ax = z.x + (nextFloat(w.rng) * 2 - 1) * z.r * 0.8;
          const ay = z.y + (nextFloat(w.rng) * 2 - 1) * z.r * 0.8;
          const localR = z.src === 'arrow_storm' || z.src === 'arrow_storm_pierce' ? z.r : 1.5;
          const dmgType =
            z.src === 'arrow_storm_pierce'
              ? DamageType.Pierce
              : z.src.startsWith('arrow_storm')
                ? DamageType.Arrow
                : DamageType.Fire;
          w.spatial.query(z.src.startsWith('arrow_storm') ? z.x : ax, z.src.startsWith('arrow_storm') ? z.y : ay, localR, u, (j) => {
            if (!z.friendlyFire && u.team[j] === z.team) return;
            damageUnit(w, j, z.power, dmgType, z.x, z.y, -1);
          });
          pushEvent(w.events, EV.Impact, ax, ay, 0.8, z.src.startsWith('arrow_storm') ? 20 : 91, z.team);
        }
        break;
      }
      case ZoneKind.Sunder:
        w.spatial.query(z.x, z.y, z.r, u, (j) => {
          if (u.team[j] === z.team) return;
          u.shieldT[j] = 0;
          u.flags[j] &= ~UF.Shielded;
          if (z.power > 0) damageUnit(w, j, z.power * dt, DamageType.Poison, z.x, z.y, -1);
        });
        break;
      case ZoneKind.Shield:
        w.spatial.query(z.x, z.y, z.r, u, (j) => {
          if (u.team[j] !== z.team) return;
          u.shieldT[j] = Math.max(u.shieldT[j], 0.3);
          u.morale[j] = Math.max(u.morale[j], 0.6);
        });
        break;
      case ZoneKind.Rally:
        w.spatial.query(z.x, z.y, z.r, u, (j) => {
          if (u.team[j] !== z.team) return;
          u.morale[j] = Math.min(1, u.morale[j] + 0.25 * dt);
        });
        break;
      default:
        break;
    }

    if (z.t <= 0) w.zones.splice(i, 1);
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────

function forEachFriendly(w: World, x: number, y: number, r: number, fn: (i: number) => void, team: number): void {
  w.spatial.query(x, y, r, w.units, (i) => {
    if (!alliedWith(w.units.team[i], team)) return;
    fn(i);
  });
}

/** Recently fallen defenders rise again, briefly. */
function graveCall(w: World, x: number, y: number, r: ReturnType<typeof resolvePower>, team: number): void {
  const cap = r.extras.includes('more') ? 16 : 10;
  const raised = summonDead(w, x, y, cap, r.duration, r.damage > 0 ? 1.6 : 1, team);
  if (raised === 0) {
    pushLog(w.events, 'Nothing here is willing. Not yet.', LogTone.Neutral);
  } else {
    pushLog(w.events, `${raised} rise, and do not ask why.`, LogTone.Lore);
  }
}

function summonDead(w: World, x: number, y: number, count: number, duration: number, hpMul: number, team: number): number {
  const type = SKELETON();
  let made = 0;
  for (let n = 0; n < count; n++) {
    const a = (n / count) * Math.PI * 2;
    const d = 0.8 + (n % 3) * 0.7;
    const sx = Math.min(MAP_W - 1, Math.max(1, x + Math.cos(a) * d));
    const sy = Math.min(MAP_H - 1, Math.max(1, y + Math.sin(a) * d));
    const i = spawnUnit(w, type, team, sx, sy);
    if (i < 0) break;
    w.units.maxHp[i] *= hpMul;
    w.units.hp[i] = w.units.maxHp[i];
    w.units.lifeT[i] = duration;
    w.units.flags[i] |= UF.Summoned;
    made++;
  }
  if (made > 0) pushEvent(w.events, EV.Spawn, x, y, made, type, team);
  return made;
}

function brambleLine(
  w: World,
  x: number,
  y: number,
  dirX: number,
  dirY: number,
  r: ReturnType<typeof resolvePower>,
  team: number,
): void {
  const type = BRAMBLE();
  // Perpendicular to the aim direction, so it reads as a barrier across a lane.
  const px = -dirY;
  const py = dirX;
  const segments = Math.max(3, Math.round(r.radius * 1.4));
  for (let n = 0; n < segments; n++) {
    const t = n - (segments - 1) / 2;
    const sx = x + px * t * 0.9;
    const sy = y + py * t * 0.9;
    if (sx < 1 || sy < 1 || sx > MAP_W - 1 || sy > MAP_H - 1) continue;
    const i = spawnUnit(w, type, team, sx, sy);
    if (i < 0) break;
    w.units.lifeT[i] = r.duration;
    w.units.maxHp[i] *= r.damage > 0 ? 1 : 1;
    w.units.hp[i] = w.units.maxHp[i];
  }
}

function cone(
  w: World,
  x: number,
  y: number,
  dirX: number,
  dirY: number,
  length: number,
  fn: (i: number, d: number) => void,
  team: number,
): void {
  const u = w.units;
  w.spatial.query(x, y, length, u, (i, d2) => {
    if (alliedWith(u.team[i], team)) return;
    const dx = u.x[i] - x;
    const dy = u.y[i] - y;
    const d = Math.sqrt(d2) || 1;
    const dot = (dx * dirX + dy * dirY) / d;
    if (dot < 0.45) return;
    fn(i, d);
  });
}

function structuresIn(
  w: World,
  x: number,
  y: number,
  r: number,
  dmg: number,
  dmgType: number,
  enemyOnly = false,
): void {
  const x0 = Math.max(0, Math.floor(x - r));
  const x1 = Math.min(MAP_W - 1, Math.ceil(x + r));
  const y0 = Math.max(0, Math.floor(y - r));
  const y1 = Math.min(MAP_H - 1, Math.ceil(y + r));
  for (let ty = y0; ty <= y1; ty++) {
    for (let tx = x0; tx <= x1; tx++) {
      if ((tx + 0.5 - x) ** 2 + (ty + 0.5 - y) ** 2 > r * r) continue;
      const k = w.map.idx(tx, ty);
      if (!enemyOnly && w.map.wall[k] !== WallKind.None) damageWall(w, k, dmg * 0.5, dmgType);
    }
  }
}

function repairWalls(w: World, x: number, y: number, r: number, amount: number): void {
  const x0 = Math.max(0, Math.floor(x - r));
  const x1 = Math.min(MAP_W - 1, Math.ceil(x + r));
  const y0 = Math.max(0, Math.floor(y - r));
  const y1 = Math.min(MAP_H - 1, Math.ceil(y + r));
  for (let ty = y0; ty <= y1; ty++) {
    for (let tx = x0; tx <= x1; tx++) {
      const k = w.map.idx(tx, ty);
      if (w.map.wall[k] === WallKind.None) continue;
      w.map.wallHp[k] = Math.min(w.map.wallMaxHp[k], w.map.wallHp[k] + amount);
    }
  }
}

/** Ally corpses left on the field are worth Souls to the Litany. */
export function harvestSouls(w: World, i: number): void {
  if (w.faction.id !== 'litany') return;
  w.res.souls += 2;
  killUnit(w, i);
}

/** Debug/UI helper: which buildings grant which powers. */
export function powersFromBuildings(w: World): string[] {
  const out = new Set<string>();
  const b = w.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.build[i] < 1 || b.team[i] !== Team.Player) continue;
    for (const g of buildingType(b.typeId[i]).grants) out.add(g);
  }
  return [...out];
}
